import { useEffect, useMemo, useState } from 'react'

import { useQuery } from '@apollo/client'
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material'

import { useAuth } from 'src/auth'
import { PACKAGING_GUIDE_FULL } from 'src/components/PackingGuide/packagingGuideGraphql'
import { PACKNING_SELLING_PERIOD_NOTICE } from 'src/components/PackingGuide/packningNoticeGraphql'
import { usePackagingGuideAccess } from 'src/components/PackingGuide/usePackagingGuideAccess'
import { getEffectiveTeamId } from 'src/utils/getEffectiveTeamId'

function formatPackningNoticePeriodEnd(isoDate: string) {
  return new Date(isoDate).toLocaleDateString('sv-SE', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  })
}

function noticeDismissStorageKey(teamId: string) {
  return `packningPreviousPeriodNoticeOk:${teamId}`
}

/**
 * Informs team lead/member that packning data is for the previous ended period
 * while a new sale is ongoing (multi-period teams only).
 */
export function PackningPreviousPeriodNoticeModal() {
  const { currentUser } = useAuth()
  const teamId = getEffectiveTeamId(currentUser)
  const { canUsePackningGuide, packagingGuideQueryVariables } =
    usePackagingGuideAccess()

  const skipNoticeQueries = !teamId || !canUsePackningGuide

  const { data: noticeData, loading: noticeLoading } = useQuery(
    PACKNING_SELLING_PERIOD_NOTICE,
    {
      variables: { teamId },
      skip: skipNoticeQueries,
    }
  )

  const { data: guideData, loading: guideLoading } = useQuery(
    PACKAGING_GUIDE_FULL,
    {
      skip: !canUsePackningGuide,
      variables: packagingGuideQueryVariables,
    }
  )

  const ctx = guideData?.packagingGuideContext
  const memberSp = noticeData?.memberSellingPeriod
  const periodCount = noticeData?.team?.sellingPeriods?.periods?.length ?? 0

  const shouldShow = useMemo(() => {
    if (!teamId || !canUsePackningGuide) return false
    const active = Boolean(memberSp && new Date() <= new Date(memberSp.end))
    if (!active) return false
    if (periodCount <= 1) return false
    if (ctx?.noEndedSellingPeriod) return false
    const end = ctx?.sellingPeriod?.end
    if (!end) return false
    return true
  }, [
    teamId,
    canUsePackningGuide,
    memberSp,
    periodCount,
    ctx?.noEndedSellingPeriod,
    ctx?.sellingPeriod?.end,
  ])

  const [open, setOpen] = useState(false)

  useEffect(() => {
    if (!teamId) {
      setOpen(false)
      return
    }
    if (!shouldShow || noticeLoading || guideLoading) {
      if (!shouldShow) {
        setOpen(false)
      }
      return
    }
    if (
      typeof window !== 'undefined' &&
      window.sessionStorage.getItem(noticeDismissStorageKey(teamId)) === '1'
    ) {
      return
    }
    setOpen(true)
  }, [teamId, shouldShow, noticeLoading, guideLoading])

  const formattedEnd = ctx?.sellingPeriod?.end
    ? formatPackningNoticePeriodEnd(ctx.sellingPeriod.end)
    : ''

  const handleOk = () => {
    if (teamId && typeof window !== 'undefined') {
      window.sessionStorage.setItem(noticeDismissStorageKey(teamId), '1')
    }
    setOpen(false)
  }

  return (
    <Dialog open={open} onClose={handleOk} maxWidth="sm" fullWidth>
      <DialogTitle>OBS!</DialogTitle>
      <DialogContent>
        <Typography>
          Datan som visas gäller er förra försäljning (avslutad {formattedEnd}).
        </Typography>
        <Typography sx={{ mt: 2 }}>
          När er nuvarande försäljning avslutas uppdateras datan.
        </Typography>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleOk} variant="contained" color="primary">
          OK
        </Button>
      </DialogActions>
    </Dialog>
  )
}
